import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  AppBar, Avatar, Box, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText,
  Menu, MenuItem, Toolbar, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ChecklistIcon from '@mui/icons-material/Checklist';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import { tokens } from '../theme';
import { useSession } from '../viewmodels/SessionViewModel';

export const NAV = [
  { to: '/chat', label: 'Chats', icon: <ChatBubbleOutlineIcon /> },
  { to: '/team', label: 'Team', icon: <GroupsOutlinedIcon /> },
  { to: '/tasks', label: 'Tasks', icon: <ChecklistIcon /> },
  { to: '/schedules', label: 'Schedules', icon: <EventRepeatIcon /> },
  { to: '/triggers', label: 'Triggers', icon: <BoltOutlinedIcon /> },
  { to: '/approvals', label: 'Approvals', icon: <FactCheckOutlinedIcon /> },
  { to: '/connections', label: 'Connections', icon: <LinkOutlinedIcon /> },
  { to: '/activity', label: 'Activity', icon: <HistoryOutlinedIcon /> },
  { to: '/settings', label: 'Settings', icon: <SettingsOutlinedIcon /> },
];

const RAIL_WIDTH = 76;

function NavList({ onNavigate, compact }) {
  const { pathname } = useLocation();
  return (
    <List sx={{ px: compact ? 0.75 : 1.5 }}>
      {NAV.map((item) => {
        const selected = pathname.startsWith(item.to);
        const button = (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            selected={selected}
            onClick={onNavigate}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              justifyContent: compact ? 'center' : 'flex-start',
              flexDirection: compact ? 'column' : 'row',
              py: compact ? 1 : 1.25,
              '&.Mui-selected': { bgcolor: tokens.primarySoft, color: 'primary.main' },
              '&.Mui-selected .MuiListItemIcon-root': { color: 'primary.main' },
            }}
          >
            <ListItemIcon sx={{ minWidth: compact ? 0 : 40, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontWeight: selected ? 600 : 500, fontSize: compact ? 11 : undefined, align: compact ? 'center' : 'left' }}
            />
          </ListItemButton>
        );
        return compact ? <Tooltip key={item.to} title={item.label} placement="right">{button}</Tooltip> : button;
      })}
    </List>
  );
}

/**
 * App frame. The chat screens use the whole width with a slim icon rail; other pages get the wider menu
 * and a centred content column.
 */
export default function AppShell() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { pathname } = useLocation();
  const chatPage = pathname.startsWith('/chat');
  const compact = chatPage && !isMobile;
  const width = compact ? RAIL_WIDTH : tokens.sidebarWidth;
  const { me, signOut } = useSession();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const name = me?.account?.displayName || 'Account';

  const drawer = (
    <Box>
      <Toolbar sx={{ gap: 1, justifyContent: compact ? 'center' : 'flex-start', px: compact ? 0 : 2 }}>
        <GroupsOutlinedIcon color="primary" />
        {!compact && <Typography variant="h6" color="primary">OrkaCrew</Typography>}
      </Toolbar>
      <Divider />
      <NavList onNavigate={() => setOpen(false)} compact={compact} />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{ ml: { md: `${width}px` }, width: { md: `calc(100% - ${width}px)` }, borderBottom: `1px solid ${tokens.border}` }}
      >
        <Toolbar variant="dense">
          {isMobile && (
            <IconButton edge="start" aria-label="Open menu" onClick={() => setOpen(true)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography fontWeight={600} sx={{ flexGrow: 1 }}>{isMobile ? 'OrkaCrew' : ''}</Typography>
          <IconButton aria-label="Account menu" onClick={(e) => setAnchor(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>{name.charAt(0).toUpperCase()}</Avatar>
          </IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
            <Box sx={{ px: 2, py: 1, maxWidth: 260 }}>
              <Typography fontWeight={600} noWrap>{name}</Typography>
              <Typography variant="body2" color="text.secondary" noWrap>{me?.account?.email || me?.account?.mobile || ''}</Typography>
              <Typography variant="caption" color="text.secondary">{me?.isOwner ? 'Owner' : 'Team member'}</Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { setAnchor(null); signOut(); }}>Sign out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: width }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? open : true}
          onClose={() => setOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: isMobile ? tokens.sidebarWidth : width, boxSizing: 'border-box', borderRight: `1px solid ${tokens.border}` } }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100vh', overflow: chatPage ? 'hidden' : 'auto' }}>
        <Toolbar variant="dense" />
        {chatPage ? (
          <Box sx={{ flexGrow: 1, minHeight: 0 }}><Outlet /></Box>
        ) : (
          <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, width: '100%', mx: 'auto' }}><Outlet /></Box>
        )}
      </Box>
    </Box>
  );
}
